import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import { Sparkles, Loader2, Download, RefreshCw, X, Image as ImageIcon, Instagram } from "lucide-react";
import { generateMoodboard, fetchMoodboard } from "../lib/api";
import { toast } from "sonner";

/**
 * MoodBoard — 3×3 AI-generated aesthetic collage for an itinerary.
 * Uses Gemini Nano Banana via /api/itinerary/{id}/moodboard with a background-job pattern:
 *  - POST kicks off the job, returns immediately.
 *  - Frontend polls GET every 2.5s; tiles stream in as they finish.
 *  - Once state==="ready", polling stops.
 * Downloadable as a single PNG (perfect for IG story share).
 */
export const MoodBoard = ({ itinerary }) => {
  const [open, setOpen] = useState(false);
  const [board, setBoard] = useState(null); // { state, tiles, tiles_total, ... }
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);
  const gridRef = useRef(null);

  const TILES_TOTAL = board?.tiles_total || 9;
  const tiles = board?.tiles || [];
  const state = board?.state;
  const isLoading = state === "generating" || polling;
  const hasReady = state === "ready" && tiles.length > 0;

  // Probe for cached moodboard on mount
  useEffect(() => {
    if (!itinerary?.id) return;
    let dead = false;
    fetchMoodboard(itinerary.id)
      .then((b) => {
        if (!dead && b) {
          setBoard(b);
          if (b.state === "generating") startPolling();
        }
      })
      .catch(() => {});
    return () => {
      dead = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itinerary?.id]);

  const startPolling = () => {
    if (pollRef.current) return;
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const b = await fetchMoodboard(itinerary.id);
        if (b) setBoard(b);
        if (b?.state === "ready" || b?.state === "error") {
          stopPolling();
          if (b.state === "ready") {
            toast.success("Mood-board ready — tap download to share");
          } else {
            toast.error(b.error || "Mood-board generation failed.");
          }
        }
      } catch (e) {
        // Soft-fail polling; keep trying
        console.error("Mood-board polling error:", e);
      }
    }, 2500);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  };

  const generate = async (regenerate = false) => {
    if (!itinerary?.id) return;
    if (!open) setOpen(true);
    try {
      const b = await generateMoodboard(itinerary.id, { regenerate });
      setBoard(b);
      if (b.state === "ready") {
        toast.success("Mood-board ready");
      } else {
        startPolling();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Mood-board generation failed. Try again.";
      toast.error(msg);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!hasReady && !isLoading) generate(false);
  };

  const download = async () => {
    if (!gridRef.current) return;
    try {
      const dataUrl = await toPng(gridRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#050505",
        skipFonts: true,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `vybe-moodboard-${itinerary.id}.png`;
      a.click();
      toast.success("Mood-board saved to your downloads");
    } catch (e) {
      toast.error("Couldn't save image. Try again.");
    }
  };

  const shareToInstagram = async () => {
    if (!gridRef.current) return;
    
    // Check if Web Share API is supported
    if (!navigator.share && !navigator.canShare) {
      toast.error("Sharing not supported on this browser. Use Download instead.");
      return;
    }
    
    try {
      const dataUrl = await toPng(gridRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#050505",
        skipFonts: true,
      });
      
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], `vybe-${itinerary.id}.png`, { type: "image/png" });
      
      // Check if sharing files is supported
      if (navigator.canShare && !navigator.canShare({ files: [file] })) {
        toast.error("Image sharing not supported. Use Download instead.");
        return;
      }
      
      await navigator.share({
        files: [file],
        title: itinerary?.title || "VYBE Mood-Board",
        text: `Check out this travel mood-board! 🗺️✨ Created with VYBE`,
      });
      
      toast.success("Shared successfully! 📸");
    } catch (e) {
      if (e.name === "AbortError") {
        // User cancelled the share
        return;
      }
      console.error("Share error:", e);
      toast.error("Couldn't share. Try Download instead.");
    }
  };

  const completedCount = tiles.length;

  return (
    <>
      <button
        type="button"
        data-testid="open-moodboard-btn"
        onClick={handleOpen}
        className="group relative overflow-hidden glass rounded-full px-5 py-3 inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-[0.18em] text-white border border-white/15 hover:border-white/40 transition active:scale-[0.98]"
      >
        <span className="relative z-10 flex items-center gap-2">
          <ImageIcon className="h-4 w-4 text-[#FFB45C]" />
          {hasReady ? "Open mood-board" : "Generate mood-board"}
        </span>
        <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent group-hover:translate-x-full transition-transform duration-700" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
            onClick={() => setOpen(false)}
            data-testid="moodboard-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-[640px] glass-strong rounded-3xl p-5 sm:p-6 ring-1 ring-white/10 max-h-[92vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between gap-3 mb-5">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#FFB45C] mb-1 inline-flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" /> AI Mood-Board
                  </div>
                  <h3 className="font-display font-black text-xl sm:text-2xl leading-tight truncate">
                    {itinerary?.title || "Your trip"}
                  </h3>
                  <p className="text-xs text-zinc-400 mt-1 truncate">
                    {board?.destination || itinerary?.destination_recommendation || itinerary?.destination}
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="moodboard-close"
                  className="rounded-full p-2 text-zinc-300 hover:text-white hover:bg-white/10 transition"
                  aria-label="Close mood-board"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div
                ref={gridRef}
                data-testid="moodboard-grid"
                className="rounded-2xl overflow-hidden bg-[#050505] p-3"
              >
                <div className="grid grid-cols-3 gap-1.5">
                  {Array.from({ length: TILES_TOTAL }).map((_, i) => {
                    const tile = tiles.find((t) => (t.idx ?? -1) === i) || tiles[i];
                    if (!tile) {
                      return <SkeletonTile key={i} index={i} active={isLoading} />;
                    }
                    const src = `data:${tile.mime || "image/png"};base64,${tile.image_b64}`;
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className="relative aspect-[4/5] overflow-hidden rounded-lg group"
                        data-testid={`moodboard-tile-${i}`}
                      >
                        <img
                          src={src}
                          alt={tile.label}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition">
                          <div className="text-[9px] font-semibold text-white truncate">{tile.label}</div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <div className="mt-3 flex items-center justify-between px-1 text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-display font-bold">
                  <span>VYBE</span>
                  <span className="text-[#FFB45C]">{board?.destination || itinerary?.destination || ""}</span>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <div className="text-[11px] text-zinc-400 font-medium">
                  {isLoading ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {completedCount}/{TILES_TOTAL} tiles ready · streaming from Nano Banana…
                    </span>
                  ) : hasReady ? (
                    <span>{completedCount} aesthetic tiles · ready to share</span>
                  ) : state === "error" ? (
                    <span className="text-red-400">Generation failed. Tap regenerate.</span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => generate(true)}
                    disabled={isLoading}
                    data-testid="moodboard-regenerate-btn"
                    className="glass rounded-full px-4 py-2.5 inline-flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-[0.18em] text-zinc-200 hover:text-white border border-white/10 hover:border-white/30 transition disabled:opacity-50"
                  >
                    {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Regenerate
                  </button>
                  <button
                    type="button"
                    onClick={shareToInstagram}
                    disabled={tiles.length === 0}
                    data-testid="moodboard-share-ig-btn"
                    className="gradient-purple-pink text-black font-display font-bold uppercase tracking-[0.18em] text-[11px] rounded-full px-4 py-2.5 inline-flex items-center gap-1.5 hover:opacity-95 active:scale-[0.98] transition disabled:opacity-50"
                  >
                    <Instagram className="h-3.5 w-3.5" />
                    Share to IG
                  </button>
                  <button
                    type="button"
                    onClick={download}
                    disabled={tiles.length === 0}
                    data-testid="moodboard-download-btn"
                    className="gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.18em] text-[11px] rounded-full px-4 py-2.5 inline-flex items-center gap-1.5 hover:opacity-95 active:scale-[0.98] transition disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const SkeletonTile = ({ index, active = true }) => (
  <div
    className={`relative aspect-[4/5] overflow-hidden rounded-lg bg-white/5 ${active ? "moodboard-shimmer" : ""}`}
    style={{ animationDelay: `${index * 0.08}s` }}
  >
    {active && (
      <div className="absolute inset-0 flex items-center justify-center">
        <Sparkles className="h-4 w-4 text-white/20 animate-pulse" />
      </div>
    )}
  </div>
);
